import I18n from 'core/i18n'
import { utilService } from 'core/services'
import * as moment from 'moment'
import * as React from 'react'
import { SingleDatePicker } from 'react-dates'
import styled, { AnyStyledComponent, createGlobalStyle } from 'styled-components'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import { Button, InputText } from '../../../atoms'
import { Form } from '../../../molecules'
import ReactSelectWrap from './ReactSelectWrap'

declare var window: interfaces.IWindow
export interface IGuideSearchParams {
  fullName: string | null | undefined
  day?: string | null | undefined
  date: moment.Moment | null | undefined
  selectedSkills: any[] | null | undefined
  selectedTourSkills: any[] | null | undefined
  selectedAreas: any[] | null | undefined
  selectedAvailability: any[] | null | undefined
}
interface IProps {
  areas?: {
    data: any[]
  }
  skills?: {
    data: any[]
  }
  tour_skills?: {
    data: any[]
  }
  startAt: string
  handleSearch(values: IGuideSearchParams): void
}

const FIELDS = {
  fullname: 'fullname',
  day: 'day',
}

const selectCustomStyles = {
  control: (provided, state) => ({
    ...provided,
    border: `1px solid ${constants.COLORS.divider}`,
    borderRadius: 4,
    '&:hover': { border: `1px solid ${constants.COLORS.primary}` },
  }),
  multiValue: (styles, { data }) => ({
    ...styles,
    backgroundColor: constants.COLORS.primary,
  }),
  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: constants.COLORS.white,
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: constants.COLORS.white,
  }),
}

const SearchFormForGuideAssign: React.FC<IProps> = ({
  areas,
  skills,
  tour_skills,
  startAt,
  handleSearch,
}) => {
  const { data: areasData } = utilService.getDataFromJson(areas)
  const { data: skillsData } = utilService.getDataFromJson(skills)
  const { data: tourSkillsData } = utilService.getDataFromJson(tour_skills)

  const [showAll, setShowAll] = React.useState<boolean>(false)
  const initDate = I18n.locale === 'en' ? moment(startAt) : moment(startAt, 'YYYY年MM月DD日 HH:mm')
  const [selectedDate, setSelectedDate] = React.useState<moment.Moment>(initDate)
  const [dateFocused, setStartDateFocused] = React.useState(null)
  const [searching, setSearching] = React.useState<boolean>(false)

  const initialParams = new URLSearchParams(window.location.search)
  const getFromParams = (paramName, targetArray) => {
    if (initialParams.has(paramName)) {
      const params = initialParams.get(paramName).split(',')
      return params.map(paramID => {
        const { id, name } = targetArray.find(item => String(item.id) === String(paramID))
        return { value: String(id), label: name }
      })
    }

    return null
  }

  const [selectedAreas, setSelectedAreas] = React.useState(getFromParams('area_ids', areasData))
  const [selectedSkills, setSelectedSkills] = React.useState(getFromParams('skill_ids', skillsData))

  const [selectedTourSkills, setSelectedTourSkills] = React.useState(
    getFromParams('tour_skill_ids', tourSkillsData)
  )
  const [selectedAvailability, setSelectedAvailability] = React.useState(
    getFromParams('time_ids', constants.TIME_OPTIONS)
  )

  const handleCalendarOutSideRange = React.useCallback(calendarMoment => {
    return false
  }, [])

  const onStartDateChange = React.useCallback(
    (date: moment.Moment | null) => {
      setSelectedDate(date || initDate)
    },
    [initDate]
  )

  const handleSubmit = React.useCallback(
    async (_initialValues, values) => {
      values.day = moment(values.day).format('YYYY-MM-DD')
      const valuesObject: IGuideSearchParams = {
        ...values,
        selectedSkills,
        selectedTourSkills,
        selectedAreas,
        selectedAvailability,
      }
      setSearching(true)
      try {
        await handleSearch(valuesObject)
      } finally {
        setSearching(false)
      }
    },
    [selectedDate, selectedAreas, selectedSkills, selectedAvailability, selectedTourSkills]
  )

  const getOptions = (arr: any[]) =>
    arr.map(item => ({
      value: item.id,
      label: item.name,
    }))

  return (
    <Form fields={FIELDS} handleSubmit={handleSubmit}>
      <S.SearchInputs>
        <InputText name="fullname" defaultValue={''} placeholder={I18n.t('generic.fullname')} />

        <DateRangePickerCustomStyle />
        <SingleDatePicker
          id="day"
          date={selectedDate}
          placeholder={I18n.t('generic.date')}
          onDateChange={onStartDateChange}
          focused={dateFocused}
          onFocusChange={changed => setStartDateFocused(changed.focused)}
          enableOutsideDays={true}
          isOutsideRange={handleCalendarOutSideRange}
          numberOfMonths={1}
          hideKeyboardShortcutsPanel={true}
        />
        {showAll && (
          <>
            <ReactSelectWrap
              title={I18n.t('generic.tour_skill')}
              placeholder={I18n.t('generic.select_tour_skill')}
              options={getOptions(tourSkillsData)}
              onChange={setSelectedTourSkills}
              defaultValue={selectedTourSkills}
            />

            <ReactSelectWrap
              title={I18n.t('generic.skill')}
              placeholder={I18n.t('generic.select_skill')}
              options={getOptions(skillsData)}
              onChange={setSelectedSkills}
              defaultValue={selectedSkills}
            />

            <ReactSelectWrap
              title={I18n.t('generic.area')}
              placeholder={I18n.t('generic.select_area')}
              options={getOptions(areasData)}
              onChange={setSelectedAreas}
              defaultValue={selectedAreas}
            />

            <ReactSelectWrap
              title={I18n.t('generic.availability')}
              placeholder={I18n.t('generic.select_availability')}
              options={constants.TIME_OPTIONS.map(time => ({
                value: time.id,
                label: time.name,
              }))}
              onChange={setSelectedAvailability}
              defaultValue={selectedAvailability}
            />
          </>
        )}
        <S.SerachButtonWrapper>
          <Button handleClick={() => setShowAll(!showAll)}>
            {showAll ? I18n.t('generic.close') : I18n.t('generic.show')}
          </Button>

          <Button primary={true} disabled={searching}>
            {I18n.t('generic.search_conversation_guide')}
          </Button>
        </S.SerachButtonWrapper>
      </S.SearchInputs>
    </Form>
  )
}

const S: { [key: string]: AnyStyledComponent } = {}
const DateRangePickerCustomStyle = createGlobalStyle`
  .SingleDatePicker {
    display: block;
    margin-top: 24px;
  }

  .DateRangePicker.DateRangePicker_1 {
    width: 100%;
  }

  .DateRangePickerInput.DateRangePickerInput_1 {
    width: 100%;
    border: none;
  }

  .DateRangePickerInput_arrow.DateRangePickerInput_arrow_1 {
    display: none;
  }

  .SingleDatePickerInput__withBorder {
    width: 100%;
    border-color: ${constants.COLORS.divider};
    border-radius: 4px;
  }

  .DateInput_input {
    font-size: 15px;
    padding: 8px 12px;
  }
  .DateInput {
    width: 100%;
  }

  .CalendarDay__selected_span {
    background: ${constants.COLORS.secondary};
    border: 1px double ${constants.COLORS.secondary};
    color: ${constants.COLORS.white};
  }

  .CalendarDay__selected {
    background: ${constants.COLORS.primary};
    border: 1px double ${constants.COLORS.primary};
    color: ${constants.COLORS.white};
  }

  .CalendarDay__selected:hover {
    background: ${constants.COLORS.primary};
    color: ${constants.COLORS.white};
  }

  .CalendarDay__hovered_span:hover,
  .CalendarDay__hovered_span {
    background: ${constants.COLORS.secondary};
    border: 1px double ${constants.COLORS.secondary};
    color: ${constants.COLORS.white};
  }
`

S.SearchInputs = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 16px;
  > * {
    width: 100%;
  }
`
S.Compact = styled.div`
  height: 16px;
  overflow: hidden;
`

S.SerachButtonWrapper = styled.div`
  margin-top: 24px;
  width: 100%;
`

export default SearchFormForGuideAssign
